// src/pages/help/topics/execution.js
// Guide « Exécution des ordres ». Contenu validé — toute modification se fait ici.
export const fr = {
  title: "Exécution des ordres",
  tagline: "Gérer ton compte MT5 depuis FullMetrix",
  readMin: 7,
  intro:
    "La section Exécution permet de gérer un compte MetaTrader 5 directement depuis FullMetrix : ouvrir des positions au marché, placer des ordres en attente, modifier les Stop Loss et Take Profit, fermer des positions, utiliser le Break-even et le Trailing Stop, calculer le risque et le ratio Risk/Reward, et convertir une distance en points vers des niveaux de prix.",
  brief: [
    "Ouvrir, modifier et fermer des positions sans quitter FullMetrix",
    "Préparer l'ordre avec Risk/Reward et Builder avant de l'envoyer",
    "Suivre les positions ouvertes et l'historique récent du compte",
  ],
  steps: [
    {
      title: "Vérifier la connexion de l'Agent",
      text: "Agent IA installé, token actif, trading algorithmique activé, WebRequest autorisé, MT5 ouvert et connecté. Si l'Agent est hors ligne, les demandes d'exécution ne partent pas.",
    },
    {
      title: "Ouvrir une position au marché",
      text: "Section Marché : sélectionne le symbole, choisis le mode de saisie du SL et du TP, renseigne SL et TP, choisis le volume, puis clique sur Buy ou Sell.",
    },
    {
      title: "Ou placer un ordre en attente",
      text: "Section En attente : type d'ordre (Buy Limit, Sell Limit, Buy Stop, Sell Stop), symbole, volume, prix d'activation, SL et TP.",
    },
    {
      title: "Gérer la position",
      text: "Sections Modifier (nouveaux SL/TP), Fermer (par symbole ou tout fermer), Break-even et Trailing Stop.",
    },
    {
      title: "Contrôler avant de valider",
      text: "Compte, symbole, sens, volume, prix d'entrée, SL, TP et nombre de positions concernées.",
    },
  ],
  tips: [
    "SL et TP peuvent être renseignés en nombre de points ou directement en prix.",
    "« Tout fermer » peut clôturer l'ensemble des positions du compte connecté : vérifie toujours le compte sélectionné avant de confirmer.",
    "Break-even et Trailing Stop aident à protéger une position, mais ne garantissent pas une clôture en gain.",
    "FullMetrix transmet les demandes vers MT5 ; l'utilisateur reste entièrement responsable de ses décisions et de sa gestion du risque.",
  ],
  faq: [
    {
      q: "Qu'est-ce que l'outil Exécution ?",
      a:
        "La section Exécution permet de gérer un compte MetaTrader 5 directement depuis FullMetrix.\n\n" +
        "Elle permet notamment de :\n" +
        "• ouvrir des positions au marché\n" +
        "• placer des ordres en attente\n" +
        "• modifier les Stop Loss et Take Profit\n" +
        "• fermer des positions\n" +
        "• utiliser le Break-even et le Trailing Stop\n" +
        "• calculer le risque et le ratio Risk/Reward\n" +
        "• convertir une distance en points vers des niveaux de prix",
    },
    {
      q: "Que faut-il faire avant de l'utiliser ?",
      a:
        "Pour envoyer des ordres vers MT5, il faut obligatoirement :\n" +
        "• avoir installé l'Agent IA MT5\n" +
        "• avoir renseigné un token actif\n" +
        "• avoir activé le trading algorithmique\n" +
        "• avoir autorisé le WebRequest de FullMetrix\n" +
        "• laisser MetaTrader 5 ouvert et connecté à Internet\n" +
        "• vérifier que l'Agent apparaît comme connecté\n\n" +
        "Si l'Agent est hors ligne, FullMetrix peut afficher les informations du compte, mais les nouvelles demandes d'exécution ne pourront pas être transmises à MT5.",
    },
    {
      q: "Comment ouvrir une position au marché ?",
      a:
        "Dans la section Marché, l'utilisateur doit :\n\n" +
        "1. sélectionner le symbole\n" +
        "2. choisir le mode de saisie du Stop Loss et du Take Profit\n" +
        "3. renseigner le SL et le TP\n" +
        "4. choisir le volume\n" +
        "5. cliquer sur Buy ou Sell\n\n" +
        "Le SL et le TP peuvent être renseignés :\n" +
        "• en nombre de points\n" +
        "• ou directement sous forme de prix précis\n\n" +
        "Des raccourcis peuvent également être proposés pour appliquer rapidement certaines distances de SL et de TP.\n\n" +
        "Avant l'envoi, vérifie toujours le symbole, le sens, le lot, le SL et le TP.",
    },
    {
      q: "Comment placer un ordre en attente ?",
      a:
        "La section En attente permet de créer un ordre différé.\n\n" +
        "L'utilisateur doit renseigner :\n" +
        "• le type d'ordre\n" +
        "• le symbole\n" +
        "• le volume\n" +
        "• le prix d'activation\n" +
        "• le Stop Loss\n" +
        "• le Take Profit\n\n" +
        "Les types d'ordres disponibles peuvent inclure :\n" +
        "• Buy Limit\n" +
        "• Sell Limit\n" +
        "• Buy Stop\n" +
        "• Sell Stop\n\n" +
        "L'ordre sera envoyé au terminal MT5 et restera en attente jusqu'à ce que les conditions prévues soient remplies.",
    },
    {
      q: "Comment modifier le Stop Loss ou le Take Profit ?",
      a:
        "La section Modifier permet d'appliquer de nouveaux niveaux de SL et de TP aux positions ouvertes.\n\n" +
        "L'utilisateur peut choisir :\n" +
        "• de modifier les positions d'un symbole précis\n" +
        "• ou, selon l'option disponible, d'appliquer la modification à plusieurs positions\n\n" +
        "Les niveaux peuvent être renseignés :\n" +
        "• en points\n" +
        "• ou directement en prix\n\n" +
        "Avant de valider, vérifie que les distances sont compatibles avec les règles du symbole et du broker.",
    },
    {
      q: "Comment fermer les positions ?",
      a:
        "La section Fermer permet de :\n" +
        "• fermer les positions d'un symbole sélectionné\n" +
        "• ou fermer toutes les positions actives\n\n" +
        "Le bouton Tout fermer doit être utilisé avec prudence, car il peut clôturer l'ensemble des positions présentes sur le compte connecté.\n\n" +
        "Vérifie toujours le compte sélectionné avant de confirmer une fermeture.",
    },
    {
      q: "À quoi servent le Break-even et le Trailing Stop ?",
      a:
        "Le Break-even permet de déplacer le Stop Loss afin de protéger une position lorsqu'elle a évolué favorablement.\n\n" +
        "Le Trailing Stop permet de faire suivre progressivement le Stop Loss selon les paramètres définis.\n\n" +
        "Ces outils peuvent aider à protéger une position, mais ils ne garantissent pas qu'elle sera clôturée en gain.\n\n" +
        "Leur fonctionnement dépend notamment :\n" +
        "• du symbole\n" +
        "• de la distance choisie\n" +
        "• de la volatilité\n" +
        "• des conditions d'exécution du broker\n" +
        "• de la connexion de l'Agent IA",
    },
    {
      q: "À quoi servent Risk/Reward et Builder ?",
      a:
        "La section Risk/Reward aide à comparer le risque prévu avec l'objectif de gain.\n\n" +
        "Elle permet de mieux visualiser le rapport entre :\n" +
        "• le prix d'entrée\n" +
        "• le Stop Loss\n" +
        "• le Take Profit\n" +
        "• le risque accepté\n" +
        "• le gain potentiel\n\n" +
        "La section Builder permet notamment de convertir une distance exprimée en points en niveaux de prix.\n\n" +
        "L'utilisateur peut renseigner :\n" +
        "• le sens de la position\n" +
        "• le prix d'entrée\n" +
        "• la distance du SL\n" +
        "• la distance du TP\n" +
        "• la taille du point\n\n" +
        "FullMetrix calcule alors les niveaux de prix correspondants.\n\n" +
        "Ces outils servent à préparer l'ordre. Ils ne garantissent pas son résultat.",
    },
    {
      q: "Où consulter les positions et l'historique ?",
      a:
        "La partie Positions ouvertes affiche les opérations actuellement actives sur le compte.\n\n" +
        "Elle peut présenter notamment :\n" +
        "• le ticket\n" +
        "• le symbole\n" +
        "• le sens de la position\n" +
        "• le volume\n" +
        "• le prix d'ouverture\n" +
        "• le Stop Loss\n" +
        "• le Take Profit\n" +
        "• le profit ou la perte en cours\n\n" +
        "La partie Historique récent permet de consulter les dernières opérations clôturées et leurs résultats.",
    },
    {
      q: "Que faire si un ordre ne s'exécute pas ?",
      a:
        "Vérifie en priorité :\n" +
        "• que l'Agent IA est connecté\n" +
        "• que le token est actif\n" +
        "• que MetaTrader 5 est ouvert\n" +
        "• que le trading algorithmique est activé\n" +
        "• que le compte MT5 est connecté\n" +
        "• que le symbole existe sur le broker\n" +
        "• que le volume respecte les limites du compte\n" +
        "• que le prix, le SL et le TP sont valides\n" +
        "• que le marché est ouvert\n" +
        "• que le compte dispose d'une marge suffisante\n\n" +
        "Un ordre peut également être refusé en raison des règles du broker ou des conditions du marché.",
    },
    {
      q: "Quelles précautions faut-il prendre ?",
      a:
        "Avant chaque action, vérifie toujours :\n" +
        "• le compte sélectionné\n" +
        "• le symbole\n" +
        "• le sens Buy ou Sell\n" +
        "• le volume\n" +
        "• le prix d'entrée\n" +
        "• le Stop Loss\n" +
        "• le Take Profit\n" +
        "• le nombre de positions concernées\n\n" +
        "FullMetrix transmet les demandes vers MT5, mais l'utilisateur reste entièrement responsable de ses décisions et de sa gestion du risque.",
    },
    {
      q: "Comment contacter le support ?",
      a:
        "Pour obtenir une réponse plus rapide, nous recommandons de contacter le support FullMargin par WhatsApp.\n\n" +
        "En cas de problème, envoie :\n" +
        "• l'adresse e-mail du compte FullMargin\n" +
        "• le compte MT5 concerné\n" +
        "• le nom du token utilisé\n" +
        "• le type d'action tentée\n" +
        "• le symbole et le volume concernés\n" +
        "• une capture de la page Exécution\n" +
        "• le message d'erreur affiché",
    },
  ],
  related: ["agent-ia-mt5", "journal-auto", "copy-trading"],
};

export const en = {
  title: "Order execution",
  tagline: "Manage your MT5 account from FullMetrix",
  readMin: 7,
  intro:
    "The Execution section lets you manage a MetaTrader 5 account directly from FullMetrix: open market positions, place pending orders, modify Stop Loss and Take Profit levels, close positions, use Break-even and Trailing Stop, compute risk and the Risk/Reward ratio, and convert a distance in points into price levels.",
  brief: [
    "Open, modify and close positions without leaving FullMetrix",
    "Prepare the order with Risk/Reward and Builder before sending it",
    "Track open positions and the account's recent history",
  ],
  steps: [
    {
      title: "Check the Agent connection",
      text: "AI Agent installed, active token, algorithmic trading enabled, WebRequest allowed, MT5 open and connected. If the Agent is offline, execution requests do not go through.",
    },
    {
      title: "Open a market position",
      text: "Market section: select the symbol, choose how to enter SL and TP, fill in SL and TP, choose the volume, then click Buy or Sell.",
    },
    {
      title: "Or place a pending order",
      text: "Pending section: order type (Buy Limit, Sell Limit, Buy Stop, Sell Stop), symbol, volume, trigger price, SL and TP.",
    },
    {
      title: "Manage the position",
      text: "Modify (new SL/TP), Close (by symbol or close all), Break-even and Trailing Stop sections.",
    },
    {
      title: "Check before confirming",
      text: "Account, symbol, direction, volume, entry price, SL, TP and the number of positions involved.",
    },
  ],
  tips: [
    "SL and TP can be entered in points or directly as prices.",
    "“Close all” can close every position on the connected account: always check the selected account before confirming.",
    "Break-even and Trailing Stop help protect a position, but never guarantee it closes in profit.",
    "FullMetrix forwards requests to MT5; you remain fully responsible for your decisions and risk management.",
  ],
  faq: [
    {
      q: "What is the Execution tool?",
      a:
        "The Execution section lets you manage a MetaTrader 5 account directly from FullMetrix.\n\n" +
        "It lets you:\n" +
        "• open market positions\n" +
        "• place pending orders\n" +
        "• modify Stop Loss and Take Profit levels\n" +
        "• close positions\n" +
        "• use Break-even and Trailing Stop\n" +
        "• compute risk and the Risk/Reward ratio\n" +
        "• convert a distance in points into price levels",
    },
    {
      q: "What do I need before using it?",
      a:
        "To send orders to MT5 you must:\n" +
        "• have installed the MT5 AI Agent\n" +
        "• have an active token filled in\n" +
        "• have enabled algorithmic trading\n" +
        "• have allowed the FullMetrix WebRequest\n" +
        "• keep MetaTrader 5 open and connected to the internet\n" +
        "• check that the Agent shows as connected\n\n" +
        "If the Agent is offline, FullMetrix can still display account information, but new execution requests cannot reach MT5.",
    },
    {
      q: "How do I open a market position?",
      a:
        "In the Market section:\n\n" +
        "1. select the symbol\n" +
        "2. choose how to enter Stop Loss and Take Profit\n" +
        "3. fill in SL and TP\n" +
        "4. choose the volume\n" +
        "5. click Buy or Sell\n\n" +
        "SL and TP can be entered:\n" +
        "• in points\n" +
        "• or directly as exact prices\n\n" +
        "Shortcuts may also be offered to apply common SL and TP distances quickly.\n\n" +
        "Before sending, always check the symbol, direction, lot, SL and TP.",
    },
    {
      q: "How do I place a pending order?",
      a:
        "The Pending section creates a deferred order.\n\n" +
        "You must fill in:\n" +
        "• the order type\n" +
        "• the symbol\n" +
        "• the volume\n" +
        "• the trigger price\n" +
        "• the Stop Loss\n" +
        "• the Take Profit\n\n" +
        "Available order types can include:\n" +
        "• Buy Limit\n" +
        "• Sell Limit\n" +
        "• Buy Stop\n" +
        "• Sell Stop\n\n" +
        "The order is sent to the MT5 terminal and stays pending until the conditions are met.",
    },
    {
      q: "How do I modify Stop Loss or Take Profit?",
      a:
        "The Modify section applies new SL and TP levels to open positions.\n\n" +
        "You can choose:\n" +
        "• to modify the positions of a specific symbol\n" +
        "• or, where available, to apply the change to several positions\n\n" +
        "Levels can be entered:\n" +
        "• in points\n" +
        "• or directly as prices\n\n" +
        "Before confirming, check the distances are compatible with the symbol and broker rules.",
    },
    {
      q: "How do I close positions?",
      a:
        "The Close section lets you:\n" +
        "• close the positions of a selected symbol\n" +
        "• or close every active position\n\n" +
        "The Close all button must be used carefully: it can close every position on the connected account.\n\n" +
        "Always check the selected account before confirming a close.",
    },
    {
      q: "What are Break-even and Trailing Stop for?",
      a:
        "Break-even moves the Stop Loss to protect a position once it has moved favourably.\n\n" +
        "Trailing Stop makes the Stop Loss follow the price progressively, according to the parameters you set.\n\n" +
        "These tools can help protect a position, but they never guarantee it closes in profit.\n\n" +
        "Their behaviour depends on:\n" +
        "• the symbol\n" +
        "• the chosen distance\n" +
        "• volatility\n" +
        "• the broker's execution conditions\n" +
        "• the AI Agent connection",
    },
    {
      q: "What are Risk/Reward and Builder for?",
      a:
        "The Risk/Reward section helps compare the planned risk with the profit target.\n\n" +
        "It shows the relationship between:\n" +
        "• the entry price\n" +
        "• the Stop Loss\n" +
        "• the Take Profit\n" +
        "• the accepted risk\n" +
        "• the potential gain\n\n" +
        "The Builder section converts a distance expressed in points into price levels.\n\n" +
        "You can enter:\n" +
        "• the direction of the position\n" +
        "• the entry price\n" +
        "• the SL distance\n" +
        "• the TP distance\n" +
        "• the point size\n\n" +
        "FullMetrix then computes the matching price levels.\n\n" +
        "These tools help prepare the order. They do not guarantee its outcome.",
    },
    {
      q: "Where do I see positions and history?",
      a:
        "The Open positions block shows the trades currently active on the account.\n\n" +
        "It can display:\n" +
        "• the ticket\n" +
        "• the symbol\n" +
        "• the direction\n" +
        "• the volume\n" +
        "• the open price\n" +
        "• the Stop Loss\n" +
        "• the Take Profit\n" +
        "• the running profit or loss\n\n" +
        "The Recent history block shows the latest closed trades and their results.",
    },
    {
      q: "What if an order does not execute?",
      a:
        "Check first:\n" +
        "• that the AI Agent is connected\n" +
        "• that the token is active\n" +
        "• that MetaTrader 5 is open\n" +
        "• that algorithmic trading is enabled\n" +
        "• that the MT5 account is connected\n" +
        "• that the symbol exists at the broker\n" +
        "• that the volume respects the account limits\n" +
        "• that price, SL and TP are valid\n" +
        "• that the market is open\n" +
        "• that the account has enough margin\n\n" +
        "An order can also be rejected because of broker rules or market conditions.",
    },
    {
      q: "What precautions should I take?",
      a:
        "Before every action, always check:\n" +
        "• the selected account\n" +
        "• the symbol\n" +
        "• the Buy or Sell direction\n" +
        "• the volume\n" +
        "• the entry price\n" +
        "• the Stop Loss\n" +
        "• the Take Profit\n" +
        "• the number of positions involved\n\n" +
        "FullMetrix forwards requests to MT5, but you remain fully responsible for your decisions and risk management.",
    },
    {
      q: "How do I contact support?",
      a:
        "For a faster answer, we recommend contacting FullMargin support on WhatsApp.\n\n" +
        "If something goes wrong, send:\n" +
        "• the email address of your FullMargin account\n" +
        "• the MT5 account involved\n" +
        "• the name of the token used\n" +
        "• the action you tried\n" +
        "• the symbol and volume involved\n" +
        "• a screenshot of the Execution page\n" +
        "• the error message displayed",
    },
  ],
  related: ["agent-ia-mt5", "journal-auto", "copy-trading"],
};
